import dayjs from "dayjs";
import {
  MONTHLY,
  QUARTERLY,
  ANNUALLY,
} from "constants/salesTaxRegistrationStatusType";
import { SALES_TAX_FILING } from "constants/addons";
import { YYYY_MM_DD } from "constants/date";
import { useGetSubscriptionsQuery } from "store/apis/subscriptions";
import { useAddProductToCartMutation } from "store/apis/products";
import {
  useStartFilingMutation,
  useStartFilingAndSubscribeMutation,
} from "store/apis/filing";
import {
  GroupSubscriptionState,
  Subscription,
} from "types/Models/subscription";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { BackendError } from "types/utils/error";
import { getBillingCartUrl } from "util/getBillingCartUrl";

const SUBSCRIBABLE_BILLING_CYCLE: Record<
  string,
  Subscription["billing_cycle"]
> = {
  [MONTHLY]: "MONTHLY",
  [QUARTERLY]: "QUARTERLY",
  [ANNUALLY]: "YEARLY",
};

type FilingPeriod = {
  filingId: string;
  startDate: string;
  endDate: string;
};

export const useStartFilingSubscription = (
  paymentSchedule: string,
  stateName: string,
  filingPeriod?: FilingPeriod
) => {
  const entityId = useCurrentEntityId();
  const { uuid: groupId } = useCurrentGroupContext();
  const { isAdmin } = useRoleBasedView();
  const { alertToast } = useToast();

  const matchingBillingCycle =
    SUBSCRIBABLE_BILLING_CYCLE[
      paymentSchedule as keyof typeof SUBSCRIBABLE_BILLING_CYCLE
    ];

  const { data: subscriptions } = useGetSubscriptionsQuery(
    { groupId, entityId, subscription_types: SALES_TAX_FILING },
    { skip: !groupId || !entityId || !matchingBillingCycle }
  );

  const subscriptionForSchedule = subscriptions?.find(
    (subscription) => subscription.billing_cycle === matchingBillingCycle
  );

  const subscribedState = subscriptionForSchedule?.group_subscription?.find(
    (subscription: GroupSubscriptionState) =>
      subscription.sales_tax_state_name === stateName
  );

  const isAlreadySubscribed = Boolean(subscribedState);

  // A period that started before the subscription itself began isn't
  // covered by it — those periods still need a one-time filing/payment.
  const isPeriodCoveredBySubscription = Boolean(
    subscribedState?.started_on &&
      filingPeriod &&
      !dayjs(filingPeriod.startDate).isBefore(dayjs(subscribedState.started_on))
  );

  const [addProductToCart, { isLoading: isAddingToCart }] =
    useAddProductToCartMutation();
  const [startFiling, { isLoading: isStartingOneTimeFiling }] =
    useStartFilingMutation();
  const [startFilingAndSubscribe, { isLoading: isStartingAndSubscribing }] =
    useStartFilingAndSubscribeMutation();

  const startOneTimeFilingCheckout = async () => {
    if (!filingPeriod) {
      return;
    }
    try {
      await startFiling({ entityId, filingId: filingPeriod.filingId }).unwrap();
      const cartUrl = getBillingCartUrl({ isAdmin, groupId, entityId });
      window.open(cartUrl, "_blank");
    } catch (error) {
      alertToast({
        message:
          (error as BackendError)?.data?.error?.message ??
          "Something went wrong",
      });
    }
  };

  // First time this state is being onboarded to the subscription: any
  // already-elapsed pending period gets added to cart individually, and
  // the subscription itself is added to cover the current period onward —
  // all from a single "Start filing" click on any row for this state.
  const startFilingAndSubscribeCheckout = async () => {
    if (!filingPeriod) {
      return;
    }
    try {
      await startFilingAndSubscribe({
        entityId,
        filingId: filingPeriod.filingId,
      }).unwrap();
      const cartUrl = getBillingCartUrl({ isAdmin, groupId, entityId });
      window.open(cartUrl, "_blank");
    } catch (error) {
      alertToast({
        message:
          (error as BackendError)?.data?.error?.message ??
          "Something went wrong",
      });
    }
  };

  const startSubscriptionCheckout = async () => {
    if (!subscriptionForSchedule) {
      return;
    }
    try {
      await addProductToCart({
        groupId,
        entityId,
        payload: {
          product_id: subscriptionForSchedule.uuid,
          product_content_type_id:
            subscriptionForSchedule.product_content_type_id,
          sales_tax_state_name: stateName,
          season: filingPeriod
            ? dayjs(filingPeriod.startDate).year()
            : undefined,
          from_date: filingPeriod
            ? dayjs(filingPeriod.startDate).format(YYYY_MM_DD)
            : undefined,
          to_date: filingPeriod
            ? dayjs(filingPeriod.endDate).format(YYYY_MM_DD)
            : undefined,
        },
      }).unwrap();
      const cartUrl = getBillingCartUrl({ isAdmin, groupId, entityId });
      window.open(cartUrl, "_blank");
    } catch (error) {
      alertToast({
        message:
          (error as BackendError)?.data?.error?.message ??
          "Something went wrong",
      });
    }
  };

  // Not subscribed at all, or the period predates the subscription's own
  // start date — either way this period is a one-time filing, not a
  // subscription-covered one.
  const shouldUseOneTimeFiling =
    isAlreadySubscribed && !isPeriodCoveredBySubscription;

  let isAddingToCartResult = isAddingToCart;
  let checkoutHandler = startSubscriptionCheckout;

  if (shouldUseOneTimeFiling) {
    isAddingToCartResult = isStartingOneTimeFiling;
    checkoutHandler = startOneTimeFilingCheckout;
  } else if (!isAlreadySubscribed) {
    isAddingToCartResult = isStartingAndSubscribing;
    checkoutHandler = startFilingAndSubscribeCheckout;
  }

  return {
    subscriptionForSchedule,
    isAlreadySubscribed,
    isPeriodCoveredBySubscription,
    shouldUseOneTimeFiling,
    isAddingToCart: isAddingToCartResult,
    startSubscriptionCheckout: checkoutHandler,
  };
};
